import { Link, useLocation } from "wouter";
import { cn } from "@/lib/utils";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { ChevronDown, ChevronRight } from "lucide-react";
import { useState } from "react";

interface SidebarLinkProps {
  href: string;
  icon: string;
  label: string;
  isActive: boolean;
  collapsed?: boolean;
}

const SidebarLink = ({
  href,
  icon,
  label,
  isActive,
  collapsed = false,
}: SidebarLinkProps) => {
  if (collapsed) {
    return (
      <li className="mb-1">
        <TooltipProvider>
          <Tooltip delayDuration={200}>
            <TooltipTrigger asChild>
              <Link href={href}>
                <div
                  className={cn(
                    "flex items-center justify-center p-2 rounded cursor-pointer",
                    isActive ? "bg-[#2B88D8]" : "hover:bg-[#2B88D8]/70",
                  )}
                >
                  <span className="material-icons text-sm">{icon}</span>
                </div>
              </Link>
            </TooltipTrigger>
            <TooltipContent side="right">
              <p>{label}</p>
            </TooltipContent>
          </Tooltip>
        </TooltipProvider>
      </li>
    );
  }

  return (
    <li className="mb-1">
      <Link href={href}>
        <div
          className={cn(
            "flex items-center px-2 py-2 rounded cursor-pointer",
            isActive ? "bg-[#2B88D8]" : "hover:bg-[#2B88D8]/70",
          )}
        >
          <span className="material-icons mr-3 text-sm">{icon}</span>
          {label}
        </div>
      </Link>
    </li>
  );
};

interface SidebarSectionProps {
  title: string;
  children: React.ReactNode;
  collapsed?: boolean;
  defaultExpanded?: boolean;
}

const SidebarSection = ({
  title,
  children,
  collapsed = false,
  defaultExpanded = true,
}: SidebarSectionProps) => {
  const [isExpanded, setIsExpanded] = useState(defaultExpanded);

  if (collapsed) {
    return (
      <div className="mb-2">
        <div className="text-xs uppercase font-semibold px-2 py-1 text-white/60">
          {title.substring(0, 1)}
        </div>
        <ul>{children}</ul>
      </div>
    );
  }

  return (
    <div className="mb-4">
      <button
        className="w-full flex items-center justify-between text-xs uppercase font-semibold px-2 py-1 text-white/80 hover:text-white"
        onClick={() => setIsExpanded(!isExpanded)}
      >
        {title}
        {isExpanded ? (
          <ChevronDown className="h-3 w-3" />
        ) : (
          <ChevronRight className="h-3 w-3" />
        )}
      </button>
      {isExpanded && <ul className="mt-1">{children}</ul>}
    </div>
  );
};

export interface SidebarProps {
  collapsed?: boolean;
}

const Sidebar = ({ collapsed = false }: SidebarProps) => {
  const [location] = useLocation();

  return (
    <div
      className={cn(
        "bg-[#0078D4] text-white flex flex-col transition-all duration-300 ease-in-out",
        collapsed ? "w-16" : "w-64",
      )}
    >
      <div
        className={cn(
          "py-3 border-b border-[#2B88D8] flex",
          collapsed ? "px-2 justify-center" : "px-4 items-center",
        )}
      >
        <div className="bg-white p-1 rounded-md flex items-center justify-center">
          <img
            src="/images/peter-lang-logo.png"
            alt="Peter Lang Logo"
            style={{ width: "100px" }}
            className="object-contain"
          />
        </div>

        {!collapsed && (
          <h1 className="text-xl font-semibold ml-3">Dataverse Integration</h1>
        )}
      </div>
      <nav
        className={cn(
          "flex-1 py-4 overflow-y-auto",
          collapsed ? "px-2" : "px-4",
        )}
      >
        {/* KUP -> HS Section */}
        <SidebarSection title="KUP → HS" collapsed={collapsed}>
          <SidebarLink
            href="/field-mappings"
            icon="sync_alt"
            label="Field Mappings"
            isActive={location === "/field-mappings" || location === "/"}
            collapsed={collapsed}
          />
          <SidebarLink
            href="/choice-mappings"
            icon="list_alt"
            label="Choice Mappings"
            isActive={location === "/choice-mappings"}
            collapsed={collapsed}
          />
          <SidebarLink
            href="/field-sync-logs"
            icon="history"
            label="Field Sync Logs"
            isActive={location === "/field-sync-logs"}
            collapsed={collapsed}
          />
        </SidebarSection>

        {/* Entity Roll Up Section */}
        <SidebarSection title="Entity Roll Up" collapsed={collapsed}>
          <SidebarLink
            href="/entity-mappings"
            icon="compare_arrows"
            label="Entity Mappings"
            isActive={location === "/entity-mappings"}
            collapsed={collapsed}
          />
          <SidebarLink
            href="/entity-sync-logs"
            icon="receipt_long"
            label="Entity Sync Logs"
            isActive={location === "/entity-sync-logs"}
            collapsed={collapsed}
          />
        </SidebarSection>

        {/* Management Tasks Section */}
        <SidebarSection title="Management Tasks" collapsed={collapsed}>
          <SidebarLink
            href="/tasks"
            icon="task_alt"
            label="Tasks"
            isActive={location === "/tasks"}
            collapsed={collapsed}
          />
          <SidebarLink
            href="/task-logs"
            icon="assignment"
            label="Task Logs"
            isActive={location === "/task-logs"}
            collapsed={collapsed}
          />
          <SidebarLink
            href="/auto-discovery"
            icon="auto_fix_high"
            label="Field Auto-Discovery"
            isActive={location === "/auto-discovery"}
            collapsed={collapsed}
          />
        </SidebarSection>

        {/* System Section */}
        <SidebarSection
          title="System"
          collapsed={collapsed}
          defaultExpanded={true}
        >
          <SidebarLink
            href="/ratemonitor"
            icon="speed"
            label="API Rate Monitor"
            isActive={location === "/ratemonitor"}
            collapsed={collapsed}
          />
        </SidebarSection>
      </nav>
    </div>
  );
};

export default Sidebar;
